/**
 * Streamed conversation turns: STT + LLM + TTS in one NDJSON stream.
 *
 * The legacy /upload round-trip renders nothing until the whole unified Gemini call
 * AND the TTS synthesis finish. This module consumes the Cloud Run proxy's
 * POST /turn/stream endpoint instead: the transcript arrives first, reply text streams
 * as deltas, and PCM16 audio chunks play through the SAME Web Audio queue the streamed
 * TTS path uses (buildDefaultPlayerDeps in ttsStream.js), so the module-level context,
 * queue, and STOP_PLAYBACK_EVENT listener stay one source of truth. The in-flight
 * fetch registers its AbortController in that same shared slot, so
 * stopStreamingPlayback() kills a turn stream exactly like it kills a TTS stream.
 *
 * Protocol (NDJSON v1), in order: meta → transcript → delta* → audio* interleaved,
 * with exactly one terminal event (done | error). ttsFailed means synthesis died but
 * text continues; didNotCatch means the clip was untranscribable (no text/audio
 * events, then a minimal done). Unknown event types are ignored (forward-compatible
 * server additions); a meta with v > 1 is a hard pre-audio failure so an old client
 * falls back to the legacy path rather than misreading a newer protocol.
 */
import { _config } from '../Constants';
import { getUrlParameter } from './urlUtils';
import {
  readNDJSONStream,
  buildDefaultPlayerDeps,
  registerActiveStreamAbort,
  unregisterActiveStreamAbort,
} from './ttsStream';
import { isStreamingHealthy, markStreamingUnhealthy } from './streamHealth';
import { createStreamDeadlines } from './streamDeadlines';

// The server streams 24kHz mono PCM16; the meta event re-announces it per stream.
const STREAM_SAMPLE_RATE = 24000;

// Highest protocol version this client understands. meta.v above this aborts the
// stream before any text or audio reaches the learner (wholesale legacy fallback).
const PROTOCOL_VERSION = 1;

/**
 * Feature flag for the streamed-turn path. URL parameter overrides config so the
 * path can be tested in production (?turn_stream=true) and killed in dev
 * (?turn_stream=false) without a deploy. Mirrors isTTSStreamingEnabled, including
 * staying off on a network that already failed a stream (streamHealth.js).
 * @returns {boolean}
 */
export function isTurnStreamingEnabled() {
  const urlOverride = getUrlParameter('turn_stream');
  if (urlOverride === 'true') return true;
  if (urlOverride === 'false') return false;
  if (!isStreamingHealthy()) return false;
  return Boolean(_config.turnStreaming);
}

/**
 * Whether this turn may use the streamed path at all.
 *
 * custom_* activities stay on the legacy path: their prompts flow through the RAG
 * pipeline the proxy doesn't implement. A missing auth user means no ID token, and
 * /turn/stream requires one — falling back before the request beats a guaranteed 401.
 *
 * @param {{activity?: string|null, authUser?: object|null}} params
 * @returns {boolean}
 */
export function isTurnStreamEligible({ activity, authUser } = {}) {
  if (!authUser) return false;
  if (typeof activity === 'string' && activity.startsWith('custom_')) return false;
  return true;
}

/**
 * Carries the two flags the caller's fallback decision hinges on:
 * - `playedAudio`: audio reached the learner's ears — re-running the turn would
 *   replay it from the start, so commit the partial turn instead of falling back.
 * - `receivedDone`: the terminal done event arrived before the failure, i.e. the
 *   turn is already committed and the error is post-commit noise.
 * `reason` (when set) feeds the turn_stream_fallback tracking event.
 */
export class TurnStreamError extends Error {
  constructor(message, { playedAudio = false, receivedDone = false, reason } = {}) {
    super(message);
    this.name = 'TurnStreamError';
    this.playedAudio = playedAudio;
    this.receivedDone = receivedDone;
    if (reason) this.reason = reason;
  }
}

/**
 * Encode a recorded Blob as base64 (no data: prefix) for the JSON request body.
 * FileReader keeps this off the main thread's string-churn path for large clips.
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      const base64 = typeof result === 'string' ? result.split(',')[1] : null;
      if (base64) {
        resolve(base64);
      } else {
        reject(new Error('Failed to encode the recording as base64'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read the recording'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Stream one conversation turn from the proxy: transcript, reply deltas, and gapless
 * PCM playback as events arrive.
 *
 * Resolves when the SERVER stream completes (audio may still be draining);
 * `callbacks.onPlaybackEnded` fires exactly once when both the done event has arrived
 * and the playback queue has drained, in either order — same gate as streamTTSAudio.
 *
 * @param {Object} options
 * @param {string} options.url - Full /turn/stream endpoint URL.
 * @param {Object} options.payload - JSON body (audioBase64, mimeType, conversation, ...).
 * @param {string} options.idToken - Firebase ID token; sent as Authorization: Bearer.
 * @param {Object} [options.callbacks]
 * @param {Function} [options.callbacks.onTranscript] - (text) transcript of the learner's clip.
 * @param {Function} [options.callbacks.onFirstDelta] - First reply text increment arrived.
 * @param {Function} [options.callbacks.onDelta] - (text) each reply text increment.
 * @param {Function} [options.callbacks.onFirstAudio] - First audio chunk scheduled.
 * @param {Function} [options.callbacks.onDidNotCatch] - Clip untranscribable; reprompt flow.
 * @param {Function} [options.callbacks.onDone] - (doneEvent) terminal done event.
 * @param {Function} [options.callbacks.onPlaybackEnded] - Stream done AND queue drained.
 * @param {Object} [options.playerDeps] - Test seam: { ensureReady, playChunk, stop }.
 * @returns {Promise<{doneEvent: object, playedAudio: boolean, ttsFailed: boolean}>}
 * @throws {TurnStreamError|Error} Always carrying `playedAudio` and `receivedDone`
 *   booleans (foreign errors get them stamped on) — the caller's single contract for
 *   choosing between partial-commit and wholesale legacy fallback.
 */
export async function streamConversationTurn({ url, payload, idToken, callbacks = {}, playerDeps } = {}) {
  const {
    onTranscript,
    onFirstDelta,
    onDelta,
    onFirstAudio,
    onDidNotCatch,
    onDone,
    onPlaybackEnded,
  } = callbacks;
  const deps = playerDeps || buildDefaultPlayerDeps();

  const abortController = new AbortController();
  registerActiveStreamAbort(abortController);
  const deadlines = createStreamDeadlines({ signal: abortController.signal });

  // Two audio flags with distinct jobs (PR review: MP3 scheduling is async, so
  // "chunk received" and "chunk audible" are different moments):
  // - audioChunkReceived (sync, at event arrival): classifies FAILURES — once a
  //   chunk is in flight, treating an error as post-audio avoids a fallback
  //   re-run overlapping audio that may land a few ms later.
  // - playedAudio (async, flips only when playChunk resolves scheduled=true):
  //   drives the RESOLUTION — an all-decode-failure turn truthfully reports no
  //   audio, so the caller's no-audio backfill fires instead of being suppressed.
  let audioChunkReceived = false;
  let playedAudio = false;
  const schedulePromises = [];
  let receivedDone = false;
  let ttsFailed = false;
  let sawDelta = false;
  let sampleRateHertz = STREAM_SAMPLE_RATE;
  let encoding = 'pcm16';
  let doneEvent = null;
  let queueEmpty = true; // nothing scheduled yet
  let playbackEndedFired = false;

  // Snapshot the flags at throw time so a pre-audio failure never claims playback.
  const fail = (message, reason) => new TurnStreamError(message, { playedAudio: audioChunkReceived, receivedDone, reason });

  // done and per-chunk onEnded can complete the pair in either order (done-then-drain
  // or drain-then-done); both funnel through this single gate.
  const maybeFirePlaybackEnded = () => {
    if (playbackEndedFired || !receivedDone || !queueEmpty) return;
    playbackEndedFired = true;
    if (onPlaybackEnded) onPlaybackEnded();
  };

  const handleEvent = (event) => {
    if (!event || typeof event !== 'object') return;

    switch (event.type) {
      case 'meta':
        if (Number.isFinite(event.v) && event.v > PROTOCOL_VERSION) {
          // Pre-audio by protocol: meta is always the first event.
          throw fail(`Unsupported turn stream protocol version ${event.v}`, 'unsupported_protocol');
        }
        if (Number.isFinite(event.sampleRateHertz)) {
          sampleRateHertz = event.sampleRateHertz;
        }
        // 'pcm16' (Gemini path, non-Latin users) or 'mp3' (Neural2 per-sentence
        // path, Western users). Unknown values fall back to pcm16 semantics.
        if (typeof event.encoding === 'string') {
          encoding = event.encoding;
        }
        break;

      case 'transcript':
        if (onTranscript) onTranscript(event.text);
        break;

      case 'delta':
        if (!sawDelta) {
          sawDelta = true;
          if (onFirstDelta) onFirstDelta();
        }
        if (onDelta) onDelta(event.text);
        break;

      case 'audio': {
        audioChunkReceived = true;
        queueEmpty = false;
        const scheduled = Promise.resolve(deps.playChunk(event.data, sampleRateHertz, (drained) => {
          queueEmpty = drained;
          maybeFirePlaybackEnded();
        }, encoding));
        schedulePromises.push(scheduled);
        // playedAudio and onFirstAudio wait for actual SCHEDULING: for MP3 the
        // decode is async and can fail or be invalidated by a stop — claiming
        // first-audio here would release the mic (and suppress the no-audio
        // fallback) for audio that never became audible. `undefined` (legacy
        // injected players) counts as success for back-compat.
        scheduled.then((ok) => {
          if (ok !== false && !playedAudio) {
            playedAudio = true;
            if (onFirstAudio) onFirstAudio();
          }
        });
        break;
      }

      case 'ttsFailed':
        // Synthesis died but the text keeps streaming; surfaced in the resolution so
        // the caller can backfill spoken audio via the unary /tts path.
        ttsFailed = true;
        break;

      case 'didNotCatch':
        if (onDidNotCatch) onDidNotCatch();
        break;

      case 'done':
        doneEvent = event;
        receivedDone = true;
        // The turn is complete: a socket that closes late is not a stall to fail on.
        deadlines.stop();
        if (onDone) onDone(event);
        maybeFirePlaybackEnded();
        break;

      case 'error':
        throw fail(event.message || 'Turn stream reported an error', 'server_error');

      default:
        // Unknown event types are forward-compatible server additions; ignore.
        break;
    }
  };

  try {
    // Pre-audio by construction: any throw here (including a still-suspended
    // AudioContext) happens before a single chunk is scheduled, keeping the
    // fallback contract.
    await deps.ensureReady();
    // Fresh playback generation: invalidates any straggler MP3 decodes from a
    // previous stream so they can't schedule into (or reorder within) this one.
    if (deps.beginSession) deps.beginSession();

    deadlines.start();
    const response = await deadlines.guard(fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: abortController.signal,
    }));

    if (!response.ok) {
      const httpError = fail(`Turn stream request failed (HTTP ${response.status})`, `http_${response.status}`);
      httpError.httpStatus = response.status;
      throw httpError;
    }
    if (!response.body || typeof response.body.getReader !== 'function') {
      throw fail('Streaming responses not supported in this browser', 'no_stream_body');
    }

    await deadlines.guard(readNDJSONStream(response.body.getReader(), deadlines.wrapHandler(handleEvent)));

    if (!doneEvent) {
      // Connection dropped without a terminal event: the turn is incomplete.
      throw fail('Turn stream ended without a done event', 'no_done_event');
    }

    // Let in-flight MP3 decodes settle (they never reject — failures resolve
    // false) so the resolution's playedAudio is truthful, not a race.
    await Promise.all(schedulePromises);

    return { doneEvent, playedAudio, ttsFailed };
  } catch (error) {
    abortController.abort();
    deps.stop();
    // A deadline expiry or network error must never surface as the AbortError our own
    // abort() produces — the caller drops the turn on AbortError (deliberate stop).
    const networkReason = deadlines.networkFailureReason(error);
    if (networkReason) {
      if (!deadlines.deliveredAnyEvent()) markStreamingUnhealthy(networkReason);
      throw fail(`Turn stream failed (${networkReason}): ${error && error.message}`, networkReason);
    }
    // Foreign errors (JSON.parse SyntaxError, AbortError) get the flags stamped on so
    // the caller has one uniform contract.
    if (error && typeof error === 'object') {
      try {
        if (typeof error.playedAudio !== 'boolean') error.playedAudio = audioChunkReceived;
        if (typeof error.receivedDone !== 'boolean') error.receivedDone = receivedDone;
      } catch (stampError) {
        // Frozen error object; the caller treats a missing flag as pre-audio.
      }
    }
    throw error;
  } finally {
    deadlines.stop();
    unregisterActiveStreamAbort(abortController);
  }
}
